import { useState } from 'react'
import { Ban, CreditCard, Download, PenLine, Send, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useInvoiceAdjustments, useMarkInvoiceAsSent } from '@/hooks/use-billing'
import { usePayments } from '@/hooks/use-payments'
import { getApiErrorMessage } from '@/lib/api'
import { downloadInvoicePdf } from '@/lib/billing'
import { formatKES, formatPaymentMethod } from '@/lib/utils'
import { CreateAdjustmentModal } from './create-adjustment-modal'
import { CreditStatusBadge } from './credit-status-badge'
import { InvoiceReadingPanel } from './invoice-reading-panel'
import { RecordPaymentModal } from './record-payment-modal'
import { VoidInvoiceModal } from './void-invoice-modal'
import type { Invoice, InvoiceStatus } from '@riverpark/types'

function StatusBadge({ status }: { status: InvoiceStatus }) {
  switch (status) {
    case 'paid':
      return <Badge variant="paid">Paid</Badge>
    case 'overdue':
      return <Badge variant="overdue">Overdue</Badge>
    case 'sent':
      return <Badge variant="pending">Sent</Badge>
    case 'voided':
      return <Badge variant="default">Voided</Badge>
    default:
      return <Badge variant="draft">Draft</Badge>
  }
}

/**
 * The draft→sent→paid/overdue lifecycle is built for bills — it's meaningless on a credit note
 * ("Sent" doesn't describe anything a credit note does). Show whether it still has balance left
 * to use instead. Voided is the one status that still applies (if a credit note is ever voided).
 */
function InvoiceStatusDisplay({ invoice }: { invoice: Invoice }) {
  if (invoice.invoiceType !== 'credit_note') return <StatusBadge status={invoice.status} />
  return <CreditStatusBadge credit={invoice} />
}

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border-2 py-2.5 last:border-0">
      <span className="font-sans text-sm text-muted-foreground">{label}</span>
      <span className="font-sans text-sm font-medium text-ink">{value}</span>
    </div>
  )
}

/** Exactly which payments cleared this invoice — reconcile right here instead of cross-referencing the separate Payments list. */
function PaymentBreakdown({ invoiceId }: { invoiceId: string }) {
  const { data, isLoading } = usePayments({ invoiceId })
  const rows = data?.data ?? []

  if (isLoading) return null
  if (rows.length === 0) return null

  return (
    <div className="mt-4 space-y-1.5">
      <p className="font-sans text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Payments applied
      </p>
      <ul className="space-y-1.5">
        {rows.map((p) => (
          <li
            key={p.id}
            className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-ink">{formatDate(p.paidAt)}</span>
              <span className="text-muted-foreground">{formatPaymentMethod(p.method)}</span>
              <span className="font-medium text-ink">{formatKES(p.amount)}</span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {p.mpesaReceiptNumber ?? p.reference ?? 'No reference recorded'}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

const ADJUSTMENT_LABEL: Record<string, string> = {
  credit_note: 'Credit note',
  supplementary: 'Supplementary charge',
}

/** Credit notes and charges raised against this invoice, so its real total is visible here. */
function AdjustmentsPanel({ invoice }: { invoice: Invoice }) {
  const { adjustments, adjustedTotal, isLoading } = useInvoiceAdjustments(invoice)
  if (isLoading || adjustments.length === 0) return null

  return (
    <div className="mt-4 space-y-1.5">
      <p className="font-sans text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Adjustments
      </p>
      <ul className="space-y-1.5">
        {adjustments.map((a) => (
          <li
            key={a.id}
            className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={
                  a.status === 'voided' ? 'text-muted-foreground line-through' : 'text-ink'
                }
              >
                {a.invoiceNumber}
              </span>
              <span className="text-muted-foreground">
                {ADJUSTMENT_LABEL[a.invoiceType] ?? a.invoiceType}
                {a.status === 'voided' && ' · voided'}
              </span>
              <span
                className={
                  a.status === 'voided'
                    ? 'text-muted-foreground line-through'
                    : 'font-medium text-ink'
                }
              >
                {a.amount > 0 ? '+' : '−'}
                {formatKES(Math.abs(a.amount))}
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between px-3 pt-1 font-sans text-sm">
        <span className="text-muted-foreground">Adjusted total</span>
        <span className="font-medium text-ink">{formatKES(adjustedTotal)}</span>
      </div>
    </div>
  )
}

export function InvoiceDetailModal({
  invoice,
  onClose,
}: {
  invoice: Invoice
  onClose: () => void
}) {
  const [recordingPayment, setRecordingPayment] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [adjusting, setAdjusting] = useState(false)
  const [markSentError, setMarkSentError] = useState<string | null>(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const hasPendingApproval = !!invoice.pendingApproval
  const canRecordPayment =
    invoice.invoiceType === 'standard' && invoice.status !== 'paid' && invoice.status !== 'voided'
  const canMarkAsSent = invoice.status === 'draft' && invoice.invoiceType === 'standard'
  const canVoid =
    !hasPendingApproval &&
    ((invoice.invoiceType === 'standard' &&
      (invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue')) ||
      // Only an unused credit note — once applied to bills it can't be voided
      (invoice.invoiceType === 'credit_note' &&
        invoice.status !== 'voided' &&
        invoice.amountPaid === 0))
  const canAdjust =
    !hasPendingApproval && invoice.invoiceType === 'standard' && invoice.status === 'paid'

  const markAsSent = useMarkInvoiceAsSent()

  async function handleMarkAsSent() {
    setMarkSentError(null)
    const confirmed = confirm(
      `Mark ${invoice.invoiceNumber} as sent? This will send an SMS to ${invoice.memberName}.`,
    )
    if (!confirmed) return
    try {
      await markAsSent.mutateAsync(invoice.id)
      onClose()
    } catch (err) {
      setMarkSentError(getApiErrorMessage(err, 'Failed to mark invoice as sent'))
    }
  }

  async function handleDownloadPdf() {
    setPdfError(null)
    setDownloadingPdf(true)
    try {
      await downloadInvoicePdf(invoice)
    } catch (err) {
      setPdfError(getApiErrorMessage(err, 'Failed to download invoice PDF'))
    } finally {
      setDownloadingPdf(false)
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="invoice-detail-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              Invoice
            </p>
            <h2 id="invoice-detail-title" className="font-serif text-xl text-ink">
              {invoice.invoiceNumber}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => void handleDownloadPdf()}
              disabled={downloadingPdf}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink disabled:opacity-50"
              aria-label="Download PDF"
              title="Download PDF"
            >
              <Download size={18} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <Row label="Status" value={<InvoiceStatusDisplay invoice={invoice} />} />
        <Row label="Member" value={invoice.memberName} />
        <Row label="Plot" value={invoice.plotNumber} />
        <Row label="Utility type" value={invoice.utilityTypeName ?? '—'} />
        <Row label="Billing period" value={invoice.billingPeriod ?? '—'} />
        <Row label="Description" value={invoice.description} />
        <Row label="Amount" value={formatKES(invoice.amount)} />
        {invoice.invoiceType === 'standard' && invoice.amountPaid > 0 && (
          <>
            <Row label="Paid so far" value={formatKES(invoice.amountPaid)} />
            <Row label="Balance" value={formatKES(invoice.balance)} />
          </>
        )}
        <Row label="Due date" value={formatDate(invoice.dueDate)} />
        <Row label="Created" value={formatDate(invoice.createdAt)} />
        <InvoiceReadingPanel invoiceId={invoice.id} />
        {invoice.invoiceType === 'standard' && <AdjustmentsPanel invoice={invoice} />}
        {invoice.amountPaid > 0 && <PaymentBreakdown invoiceId={invoice.id} />}
        {invoice.pendingApproval && (
          <p className="mt-4 rounded border border-status-pending-bg bg-status-pending-bg px-4 py-2.5 font-sans text-sm text-status-pending-text">
            A change to this invoice is pending committee/admin approval.
          </p>
        )}
        {markSentError && (
          <p className="mt-3 font-sans text-xs text-destructive" role="alert">
            {markSentError}
          </p>
        )}
        {pdfError && (
          <p className="mt-3 font-sans text-xs text-destructive" role="alert">
            {pdfError}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {canMarkAsSent && (
          <Button
            variant="secondary"
            loading={markAsSent.isPending}
            onClick={() => void handleMarkAsSent()}
          >
            <Send size={14} className="mr-1.5" />
            Mark as sent
          </Button>
        )}
        {canVoid && (
          <Button variant="destructive" onClick={() => setVoiding(true)}>
            <Ban size={14} className="mr-1.5" />
            Void
          </Button>
        )}
        {canAdjust && (
          <Button variant="secondary" onClick={() => setAdjusting(true)}>
            <PenLine size={14} className="mr-1.5" />
            Add adjustment
          </Button>
        )}
        {canRecordPayment && (
          <Button onClick={() => setRecordingPayment(true)}>
            <CreditCard size={14} className="mr-1.5" />
            Record payment
          </Button>
        )}
      </ModalFooter>

      {recordingPayment && (
        <RecordPaymentModal
          memberId={invoice.memberId}
          memberName={invoice.memberName}
          presetInvoiceId={invoice.id}
          presetInvoiceNumber={invoice.invoiceNumber}
          onClose={() => setRecordingPayment(false)}
        />
      )}
      {voiding && (
        <VoidInvoiceModal
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoiceNumber}
          isCreditNote={invoice.invoiceType === 'credit_note'}
          onClose={() => setVoiding(false)}
          onVoided={onClose}
        />
      )}
      {adjusting && (
        <CreateAdjustmentModal
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoiceNumber}
          invoiceAmount={invoice.amount}
          memberId={invoice.memberId}
          onClose={() => setAdjusting(false)}
          onCreated={onClose}
        />
      )}
    </Modal>
  )
}
